import { computed, signal } from '@angular/core';

type MutationStatus = 'idle' | 'mutating' | 'success' | 'error';


type MutationOptions<Response, Request = unknown, Setup = unknown> = {
    /**
     * The setup function that initializes any necessary state or configuration before the mutation.
     */
    setup?: () => Setup;

    /**
     * The cleanup function that runs after the mutation, regardless of success or failure.
     */
    cleanup?: (setup: Setup) => void;

    /**
     * The loader function that performs the async operation.
     *
     * @param request - The request parameter passed to the loader function.
     */
    loader: (request: Request, setup: Setup) => Promise<Response>;

    /**
     * Optional callback invoked on error.
     *
     * @param error - The error encountered during the mutation.
     */
    onError?: (error: unknown, setup: Setup) => void;

    /**
     * Optional callback invoked on success.
     *
     * @param response - The response returned from the successful mutation.
     */
    onSuccess?: (response: Response, setup: Setup) => void;
}

/**
 * A simple mutation utility for handling async operations with status tracking. Similar to Angular's resource signals.
 * @see https://angular.dev/guide/signals/resource
 *
 * @param options - The mutation options including the loader function and optional success and error callbacks.
 */
export const mutation = <Response, Request, Setup = unknown>(options: MutationOptions<Response, Request, Setup>) => {
    const status = signal<MutationStatus>('idle');
    const result = signal<Response | undefined>(undefined);
    const error = signal<unknown>(undefined);
    const isMutating = computed(() => status() === 'mutating');
    const hasResult = computed(() => status() === 'success' && result() !== undefined);

    const readonlyProperties = {
        status: status.asReadonly(),
        result: result.asReadonly(),
        error: error.asReadonly(),
        isMutating,
        hasResult
    };

    return {
        ...readonlyProperties,
        mutate: async (request: Request) => {
            status.set('mutating');
            error.set(undefined);
            result.set(undefined);
            const setup = options.setup ? options.setup() : undefined as unknown as Setup;

            try {
                const res = await options.loader(request, setup);
                result.set(res);
                status.set('success');
                options.onSuccess?.(res, setup);
                return res;
            } catch (err) {
                error.set(err);
                status.set('error');
                options.onError?.(err, setup);
                throw err;
            } finally {
                options.cleanup?.(setup);
            }
        },
        asReadonly: () => readonlyProperties
    };
};
